import { BehaviorSubject, ReplaySubject } from 'rxjs';

import { Component, Input, Output, EventEmitter, inject } from '@angular/core';

import { IFsLprProviderInterface } from '../../../../../api/interfaces';
import { ViLPRProvider } from '../../../../../shared/models';
import { extractCameraVendorAndModel, WWA_VENDOR_SEPARATOR } from '../../../../../utils';
import { AXON_VENDOR_NAME } from '../../helpers';
import {
  LPRCameraModelsSelection,
  LPRProviderSelection,
  AxonViLPRSearchEntity,
} from '../../models';
import { LPRSourceSelectService } from '../../services';
import { LPRSourceConflictsComponent } from '../lpr-source-conflicts/lpr-source-conflicts.component';
import { LPRSourceDropdownComponent } from '../lpr-source-dropdown/lpr-source-dropdown.component';
import { LPRSourcePartialMatchesComponent } from '../lpr-source-partial-matches/lpr-source-partial-matches.component';

/**
 * Main source selection UI for SDM
 * Combines dropdown (source and camera selection), partial matches toggle, and conflict warnings
 * Transforms camera model selections into search parameters for parent component
 */
@Component({
  selector: 'fs-lpr-source-select',
  standalone: true,
  templateUrl: './lpr-source-select.component.html',
  styleUrls: ['./lpr-source-select.component.scss'],
  imports: [
    LPRSourceDropdownComponent,
    LPRSourcePartialMatchesComponent,
    LPRSourceConflictsComponent,
  ],
  providers: [LPRSourceSelectService],
})
export class LPRSourceSelectComponent {
  @Input()
  set providers(value: IFsLprProviderInterface[]) {
    this.providers$.next(value);
  }

  @Input() isDisabled: boolean = false;

  readonly lprSourceSelectService = inject(LPRSourceSelectService);

  providers$ = new ReplaySubject<IFsLprProviderInterface[]>(1);
  shouldShowPartialMatches$ = new BehaviorSubject<boolean>(false);

  @Output() providerSelectionChange = new EventEmitter<LPRProviderSelection>();
  providerSelectionChange$ = this.providerSelectionChange.asObservable();

  @Output() cameraModelSelectionChange = new EventEmitter<AxonViLPRSearchEntity[]>();
  @Output() partialMatchesDisplayChange = new EventEmitter<boolean>();

  private axonViLPRSearchEntities: AxonViLPRSearchEntity[] = [];

  constructor() {
    this.lprSourceSelectService.initialize(this.providers$);
  }

  onPartialMatchesDisplayChange(event: boolean): void {
    this.shouldShowPartialMatches$.next(event);
    this.partialMatchesDisplayChange.emit(event);
  }

  onProviderSelectionChange(event: LPRProviderSelection): void {
    this.providerSelectionChange.emit(event);
  }

  // Only Axon VI LPR triggers camera model selection change events
  onCameraModelSelectionChange(event: LPRCameraModelsSelection): void {
    this.setAxonViLPRSearchEntities(event);

    this.cameraModelSelectionChange.emit(this.axonViLPRSearchEntities);
  }

  private setAxonViLPRSearchEntities(event: LPRCameraModelsSelection): void {
    const currentSelection = this.getCurrentSelection(event.providerName);
    const changedSelection = this.getChangedSelection(event);
    const selection = this.getUpdatedSelection(
      currentSelection,
      changedSelection,
      event.cameraVendor,
    );

    const newEntities = this.getSearchEntities(selection, event.providerName);

    this.axonViLPRSearchEntities = [
      ...this.axonViLPRSearchEntities.filter((e) => e.providerName !== event.providerName),
      ...newEntities,
    ];
  }

  private getCurrentSelection(providerName: ViLPRProvider): Map<string, Set<string>> {
    return new Map<string, Set<string>>(
      this.axonViLPRSearchEntities
        .filter((e) => e.providerName === providerName)
        .map((e) => [e.cameraVendor, new Set(e.cameraModels)]),
    );
  }

  private getChangedSelection(event: LPRCameraModelsSelection): Map<string, Set<string>> {
    const changedSelection = new Map<string, Set<string>>();

    event.cameraModels.forEach((isSelected, rawModel) => {
      const { vendor, camera } = extractCameraVendorAndModel(rawModel, event.cameraVendor);

      let finalVendor = vendor;
      if (!finalVendor) {
        const isWWAFormat = rawModel.includes(WWA_VENDOR_SEPARATOR);
        if (event.providerName === ViLPRProvider.AXON_VI && !isWWAFormat) {
          finalVendor = AXON_VENDOR_NAME;
        } else {
          return;
        }
      }

      if (!changedSelection.has(finalVendor)) {
        changedSelection.set(finalVendor, new Set<string>());
      }

      if (isSelected) {
        const set = changedSelection.get(finalVendor);
        if (set) {
          set.add(camera);
        }
      }
    });

    return changedSelection;
  }

  private getUpdatedSelection(
    currentSelection: Map<string, Set<string>>,
    changedSelection: Map<string, Set<string>>,
    cameraVendor: string | undefined,
  ): Map<string, Set<string>> {
    changedSelection.forEach((newSelection, vendor) => {
      currentSelection.set(vendor, newSelection);
    });

    if (cameraVendor && !currentSelection.has(cameraVendor)) {
      currentSelection.set(cameraVendor, new Set());
    }

    return currentSelection;
  }

  private getSearchEntities(
    vendorSelections: Map<string, Set<string>>,
    providerName: ViLPRProvider,
  ): AxonViLPRSearchEntity[] {
    return Array.from(vendorSelections.entries())
      .filter(([cameraVendor]) => cameraVendor !== undefined)
      .map(([cameraVendor, models]) => ({
        providerName,
        cameraVendor,
        cameraModels: Array.from(models),
      }));
  }
}
